/**
 * クーポンPIN入力コンポーネント（新バーコード形式対応）
 * テンキー入力で簡単にクーポンを適用
 * 形式: [種別2桁]-[ユーザーID可変1-4桁16進]-[連番2桁16進]
 * 例: 2B-1-00, 2B-10F-A1, 2B-2AF5-A1
 */

import React, { useState, useEffect } from 'react';
import { X, Check, AlertCircle } from 'lucide-react';
import apiClient from '../../utils/apiClient';

interface CouponInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCouponValidated: (couponData: any) => void;
  subtotal: number;
}

const CouponInputModal: React.FC<CouponInputModalProps> = ({
  isOpen,
  onClose,
  onCouponValidated,
  subtotal
}) => {
  const [pin, setPin] = useState('');
  const [couponInfo, setCouponInfo] = useState<any>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [scannerInput, setScannerInput] = useState('');

  // キーパッドの配置（16進数対応: A-F横並び）
  const numpad = [
    ['A', 'B', 'C', 'D', 'E', 'F'],
    ['1', '2', '3', '-'],
    ['4', '5', '6', '←'],
    ['7', '8'],
    ['', '9', '0']
  ];

  useEffect(() => {
    if (!isOpen) {
      // モーダルが閉じたらリセット
      setPin('');
      setCouponInfo(null);
      setError('');
      setShowConfirm(false);
      setScannerInput('');
    }
  }, [isOpen]);

  // バーコードスキャナーからの入力処理
  const handleScannerInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setScannerInput(value);
    setPin(value);
    setError('');
  };

  // Enterキーで自動検証（バーコードスキャナー対応）
  const handleScannerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && scannerInput) {
      e.preventDefault();
      validatePin();
    }
  };

  // PIN入力処理
  const handleKeyPress = (key: string) => {
    if (key === '←') {
      setPin(prev => prev.slice(0, -1));
      setScannerInput(prev => prev.slice(0, -1));
      setError('');
    } else if (key === 'クリア') {
      setPin('');
      setScannerInput('');
      setError('');
      setCouponInfo(null);
    } else if (key === 'OK') {
      validatePin();
    } else if (key) {
      // 桁数制限なし（数字なら何桁でもOK）
      setPin(prev => prev + key);
      setScannerInput(prev => prev + key);
      setError('');
    }
  };

  // PIN検証（新バーコード形式対応）
  const validatePin = async () => {
    if (!pin) {
      setError('番号を入力してください');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // 新API: /coupons/validate（barcode形式で送信）
      const response = await apiClient.post('/coupons/validate', {
        barcode: pin.toUpperCase()
      });

      if (response.data.success) {
        const couponData = response.data.coupon;
        const discountType = couponData.discount_type ?? couponData.custom_discount_type;
        const discountValue = couponData.discount_value ?? couponData.custom_discount_value;
        const isDiscountCoupon =
          (discountType === 'fixed' || discountType === 'percentage') &&
          Number.isFinite(Number(discountValue));

        if (!isDiscountCoupon) {
          setError('値引きクーポンではありません');
          setCouponInfo(null);
          setShowConfirm(false);
          setLoading(false);
          return;
        }

        // 最低購入金額チェック
        if (couponData.minimum_purchase && subtotal < couponData.minimum_purchase) {
          setError(`最低購入金額は¥${couponData.minimum_purchase.toLocaleString()}です`);
          setLoading(false);
          return;
        }

        setCouponInfo(couponData);
        setShowConfirm(true);
      } else {
        setError(response.data.message || response.data.error || 'クーポンが見つかりません');
      }
    } catch (err: any) {
      console.error('Coupon validation error:', err);
      setError(err.response?.data?.error || err.response?.data?.message || '通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  // クーポン適用（新API対応）
  const applyCoupon = async () => {
    if (!couponInfo) return;

    setLoading(true);
    try {
      // クーポン情報を親コンポーネントに渡す
      onCouponValidated(couponInfo);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || '適用に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // PIN入力のヒント表示
  const getPinHint = () => {
    if (!pin) return 'クーポン番号を入力してください';

    return `入力中... (${pin.length}桁)`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        {/* ヘッダー */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">クーポン番号入力</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {!showConfirm ? (
          <>
            {/* バーコードスキャナー入力欄（非表示だがフォーカス） */}
            <input
              type="text"
              value={scannerInput}
              onChange={handleScannerInput}
              onKeyDown={handleScannerKeyDown}
              autoFocus
              placeholder="バーコードをスキャン、またはテンキーで入力"
              className="w-full p-3 border-2 border-blue-400 rounded-lg mb-4 text-center text-lg font-mono"
            />

            {/* PIN表示エリア */}
            <div className="bg-gray-100 rounded-lg p-4 mb-4">
              <div className="text-3xl font-bold text-center mb-2">
                {pin || '- - - -'}
              </div>
              <div className="text-sm text-gray-600 text-center">
                {getPinHint()}
              </div>
            </div>

            {/* エラー表示 */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                <span className="text-red-700">{error}</span>
              </div>
            )}

            {/* テンキーパッド（16進数対応） */}
            <div className="space-y-2">
              {/* A-F行（6列） */}
              <div className="grid grid-cols-6 gap-2">
                {numpad[0].map((key, colIndex) => (
                  <button
                    key={`0-${colIndex}`}
                    onClick={() => handleKeyPress(key)}
                    disabled={loading}
                    className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-2 border-purple-300 p-4 rounded-lg font-bold text-xl"
                  >
                    {key}
                  </button>
                ))}
              </div>

              {/* 1-←行（4列） */}
              {numpad.slice(1, 3).map((row, rowIndex) => (
                <div key={rowIndex + 1} className="grid grid-cols-4 gap-2">
                  {row.map((key, colIndex) => (
                    <button
                      key={`${rowIndex + 1}-${colIndex}`}
                      onClick={() => handleKeyPress(key)}
                      disabled={loading}
                      className={`
                        ${key === '←' ? 'bg-yellow-100 hover:bg-yellow-200 border-2 border-yellow-300' : 'bg-white hover:bg-gray-100 border-2 border-gray-300'}
                        ${key === '-' ? 'bg-gray-200 hover:bg-gray-300' : ''}
                        p-4 rounded-lg font-bold text-xl
                      `}
                    >
                      {key}
                    </button>
                  ))}
                </div>
              ))}

              {/* 7, 8, 9行 + OKボタン開始 */}
              <div className="grid grid-cols-4 gap-2">
                <button
                  onClick={() => handleKeyPress('7')}
                  disabled={loading}
                  className="bg-white hover:bg-gray-100 border-2 border-gray-300 p-4 rounded-lg font-bold text-xl"
                >
                  7
                </button>
                <button
                  onClick={() => handleKeyPress('8')}
                  disabled={loading}
                  className="bg-white hover:bg-gray-100 border-2 border-gray-300 p-4 rounded-lg font-bold text-xl"
                >
                  8
                </button>
                <button
                  onClick={() => handleKeyPress('9')}
                  disabled={loading}
                  className="bg-white hover:bg-gray-100 border-2 border-gray-300 p-4 rounded-lg font-bold text-xl"
                >
                  9
                </button>
                {/* OKボタン（縦2マス） - 1行目 */}
                <button
                  onClick={validatePin}
                  disabled={loading || !pin}
                  className="row-span-2 bg-green-500 hover:bg-green-600 text-white border-2 border-green-500 shadow-lg p-6 rounded-lg font-bold text-2xl"
                >
                  OK
                </button>

                {/* クリアボタン（横2マス）、0ボタン */}
                <button
                  onClick={() => handleKeyPress('クリア')}
                  disabled={loading}
                  className="col-span-2 bg-gray-300 hover:bg-gray-400 border-2 border-gray-400 p-4 rounded-lg font-bold text-xl"
                >
                  クリア
                </button>
                <button
                  onClick={() => handleKeyPress('0')}
                  disabled={loading}
                  className="bg-white hover:bg-gray-100 border-2 border-gray-300 p-4 rounded-lg font-bold text-xl"
                >
                  0
                </button>
                {/* OKボタン（縦2マス） - 2行目は自動 */}
              </div>
            </div>
          </>
        ) : (
          /* 確認画面 */
          <div className="py-4">
            {/* 会員名確認セクション（強調表示） */}
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6 mb-4">
              <div className="text-center">
                <div className="text-xl font-bold text-yellow-800 mb-3">
                  ⚠️ 会員名確認
                </div>
                <div className="text-4xl font-bold text-yellow-900 mb-3 py-4 bg-white rounded-lg shadow-inner">
                  {couponInfo.member_name}様
                </div>
                <div className="text-sm text-yellow-800 font-semibold bg-yellow-100 rounded px-4 py-2">
                  必ずお客様に会員名を確認してください
                </div>
              </div>
            </div>

            {/* クーポン詳細 */}
            <div className="bg-green-50 rounded-lg p-6 mb-4">
              <div className="text-center mb-4">
                <div className="text-lg text-gray-600 mb-2">適用クーポン</div>
                <div className="text-xl text-blue-600 font-bold">
                  {couponInfo.coupon_name}
                </div>
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span>現在の合計:</span>
                  <span className="font-bold">¥{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-red-600">
                  <span>割引額:</span>
                  <span className="font-bold">
                    {couponInfo.discount_type === 'percentage'
                      ? `${couponInfo.discount_value}%OFF`
                      : `¥${couponInfo.discount_value}`
                    }
                  </span>
                </div>
                <div className="flex justify-between text-xl font-bold border-t pt-2">
                  <span>割引後:</span>
                  <span>
                    ¥{(subtotal - (couponInfo.discount_type === 'percentage'
                      ? Math.floor(subtotal * couponInfo.discount_value / 100)
                      : couponInfo.discount_value
                    )).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowConfirm(false);
                  setCouponInfo(null);
                  setPin('');
                }}
                className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-bold"
              >
                キャンセル
              </button>
              <button
                onClick={applyCoupon}
                disabled={loading}
                className="flex-1 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-bold flex items-center justify-center"
              >
                <Check className="w-5 h-5 mr-2" />
                適用する
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CouponInputModal;
